import Vue from 'vue';
import api from './api';

/**
 * Kit de marque de l'instance (white-label), chargé depuis /api/config/app
 * au démarrage. Les valeurs Educ360 servent de défauts tant que la
 * config n'est pas chargée (et pour le pilote educ360.bj sans config).
 *
 * Usage dans les composants : this.$brand.appName, this.$modules.votes,
 * this.$txt('homeTitle') pour les textes personnalisables.
 * Usage hors composant : import { brand, modules, txt } from '@/services/brand'
 */
export const brand = Vue.observable({
  appName: 'Educ360',
  shortName: 'Educ360',
  description: 'Plateforme de services, paiements et actualités pour les écoles.',
  tagline: 'pour les écoles du Bénin',
  logoUrl: '/logo.png',
  faviconUrl: '/favicon.png',
  primaryColor: '#0A5273',
  themeColor: '#0A5273',
  publicUrl: '',
  theme: 'classique',
});

export const modules = Vue.observable({
  manuels: true,
  schools: true,
  news: true,
  trainings: true,
  votes: true,
  patronat: true,
  payments: true,
  partners: true,
});

/**
 * Textes personnalisables. Chaque clé a un défaut (fonction recevant la
 * marque, pour interpoler le nom). Le Kit de marque ne remplace que ce
 * que l'organisation veut changer.
 */
export const DEFAULT_TEXTS = {
  'homeTitle': () => 'La plateforme de services',
  'homeIntro': (b) =>
    `${b.appName} permet aux écoles de profiter de tous les services offerts par leur organisation (cotisations, abonnements, carte scolaire, notes, etc.).`,
  'homeJoinCta': (b) => `Rejoindre ${b.appName}`,
  'homeBenefitsTitle': () => 'Les avantages pour les écoles',
  'homeBenefitsIntro': () =>
    'Un seul endroit pour gérer cotisations, abonnements, carte scolaire, notes et bien plus.',
  'footerAbout': (b) => b.description,
  'footerPartnership': () => '',
  'homeBureauIntro': (b) =>
    `Le bureau de ${b.appName} réunit les acteurs qui portent la représentation, la coordination et la défense des intérêts des écoles membres.`,
};

/** Cartes de services affichées sur la page d'accueil (défauts Educ360). */
export const DEFAULT_SERVICES = [
  { icon: 'fa-file-invoice-dollar', title: 'Cotisations', text: 'Gérez les cotisations de votre école de manière centralisée et transparente.' },
  { icon: 'fa-calendar-check', title: 'Abonnements', text: 'Suivez et renouvelez les abonnements liés à votre organisation.' },
  { icon: 'fa-address-card', title: 'Carte scolaire', text: 'Créez et diffusez les carte scolaire de votre établissement.' },
  { icon: 'fa-graduation-cap', title: 'Gestion des notes', text: 'Accédez aux outils de gestion des notes et bulletins.' },
  { icon: 'fa-building', title: 'Organisations', text: 'Une organisation principale et des organisations enfantes pour structurer vos écoles.' },
  { icon: 'fa-key', title: 'Code court', text: 'Chaque école dispose d’un code court (ex. AAA123) pour un accès simple et sécurisé.' },
];

export const content = Vue.observable({});

/**
 * Cartes de services de l'accueil : personnalisées via la clé
 * homeServices du Kit (JSON [{icon,title,text}]), sinon défauts.
 */
export function homeServices() {
  try {
    const parsed = JSON.parse(content.homeServices || 'null');
    if (Array.isArray(parsed) && parsed.length) {
      return parsed
        .filter((s) => s && s.title)
        .map((s) => ({ icon: s.icon || 'fa-circle-check', title: s.title, text: s.text || '' }));
    }
  } catch (e) {
    // JSON invalide : défauts
  }
  return DEFAULT_SERVICES;
}

export function txt(key) {
  if (content[key]) return content[key];
  const def = DEFAULT_TEXTS[key];
  return def ? def(brand) : '';
}

export async function loadBrand() {
  try {
    const { data } = await api.get('/config/app');
    Object.assign(brand, data.branding || {});
    Object.assign(modules, data.modules || {});
    Object.assign(content, data.content || {});
  } catch (e) {
    // Défauts conservés — l'app reste utilisable si l'endpoint est indisponible
  }
  applyBrandToDocument();
  return brand;
}

/* ————— Palette dérivée : une couleur choisie, le reste est calculé ————— */

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
/** Assombrit (facteur < 1) ou éclaircit vers le blanc (mix > 0). */
const darken = ([r, g, b], f) => [clamp(r * f), clamp(g * f), clamp(b * f)];
const tint = ([r, g, b], mix) => [
  clamp(r + (255 - r) * mix),
  clamp(g + (255 - g) * mix),
  clamp(b + (255 - b) * mix),
];

function applyBrandToDocument() {
  if (typeof document === 'undefined') return;
  document.title = brand.appName;
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute('content', brand.themeColor);
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon && brand.faviconUrl) favicon.setAttribute('href', brand.faviconUrl);

  const root = document.documentElement;
  const rgb = hexToRgb(brand.primaryColor);
  if (rgb) {
    const dark = darken(rgb, 0.78);
    const soft = tint(rgb, 0.92);
    root.style.setProperty('--brand-primary', brand.primaryColor);
    root.style.setProperty('--brand-primary-rgb', rgb.join(' '));
    root.style.setProperty('--brand-primary-dark-rgb', dark.join(' '));
    root.style.setProperty('--brand-primary-soft-rgb', soft.join(' '));
  }
  root.setAttribute('data-theme', brand.theme || 'classique');
}

Vue.prototype.$brand = brand;
Vue.prototype.$modules = modules;
Vue.prototype.$txt = txt;
